import {
    getApiErrorMessage,
    getLibraryFolders,
    IApiAxiosError,
    ILibraryFolder,
    ILibraryListItem,
    LibraryTab,
} from "@/api";
import { Modal } from "@/components/shared/Modal";
import { useScopesEnabled } from "@/contexts/frontend/organization/useScopesEnabled";
import { DocumentList } from "@/pages/frontend/documents/components/DocumentList";
import { FilterBar } from "@/pages/frontend/documents/components/FilterBar";
import { isProtectedRootListItem } from "@/pages/frontend/documents/utils/folderTreeUtils";
import { formatItemSummary } from "@/pages/frontend/documents/utils/formatItemSummary";
import { invalidateLibraryQueries } from "@/pages/frontend/documents/utils/invalidateLibraryQueries";
import {
    LibraryTypeFilter,
    parseTypeFilter,
} from "@/pages/frontend/documents/utils/libraryItemTypes";
import { moveItemById } from "@/pages/frontend/documents/utils/moveItemById";
import { Box, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MoveToFolderModalProps } from "./MoveToFolderModal.types";

const noop = () => {};

/** BFS to collect folder IDs that must not be selectable (prevents cycles). */
const buildExcludeIds = (
    items: ILibraryListItem[],
    allFolders: ILibraryFolder[]
): Set<number> => {
    const childrenOf = new Map<number, number[]>();
    for (const f of allFolders) {
        if (f.parent_id != null) {
            let arr = childrenOf.get(f.parent_id);
            if (!arr) {
                arr = [];
                childrenOf.set(f.parent_id, arr);
            }
            arr.push(f.id);
        }
    }
    const result = new Set<number>();
    const queue: number[] = [];
    for (const i of items) {
        if (i.type === "folder" && !result.has(i.id)) {
            result.add(i.id);
            queue.push(i.id);
        }
    }
    while (queue.length > 0) {
        const id = queue.pop()!;
        for (const childId of childrenOf.get(id) ?? []) {
            if (!result.has(childId)) {
                result.add(childId);
                queue.push(childId);
            }
        }
    }
    return result;
};

const getInitialTab = (
    items: ILibraryListItem[],
    folders: ILibraryFolder[]
): LibraryTab => {
    if (items.length === 0) return "documents";
    const folderMap = new Map(folders.map((f) => [f.id, f]));
    const allKB = items.every((item) => {
        if (item.type === "folder") return item.is_knowledge_base;
        if (!item.library_folder_id) return false;
        return (
            folderMap.get(item.library_folder_id)?.is_knowledge_base ?? false
        );
    });
    return allKB ? "knowledge_base" : "documents";
};

const findKbRootId = (folders: ILibraryFolder[]): number | null => {
    const kbRoot = folders.find(
        (f) => f.is_knowledge_base && f.parent_id === null
    );
    return kbRoot?.id ?? null;
};

const getCommonFolderId = (items: ILibraryListItem[]): number | null => {
    if (items.length === 0) return null;
    const first =
        items[0].type === "folder"
            ? items[0].parent_id
            : items[0].library_folder_id;
    const allSame = items.every((item) => {
        const fid =
            item.type === "folder" ? item.parent_id : item.library_folder_id;
        return fid === first;
    });
    return allSame ? first : null;
};

export const MoveToFolderModal = ({
    open,
    onClose,
    items,
    excludedItems,
    onMoved,
}: MoveToFolderModalProps) => {
    const queryClient = useQueryClient();

    const { data: foldersData } = useQuery({
        queryKey: ["frontend-library-folders"],
        queryFn: () => getLibraryFolders(),
        enabled: open,
        staleTime: 60_000,
    });
    const folders = useMemo(
        () => foldersData?.folders ?? [],
        [foldersData?.folders]
    );
    const kbRootId = useMemo(() => findKbRootId(folders), [folders]);

    const [activeTab, setActiveTab] = useState<LibraryTab>("documents");
    const [folderId, setFolderId] = useState<number | null>(null);
    const [searchValue, setSearchValue] = useState("");
    const [selectedTypeFilter, setSelectedTypeFilter] =
        useState<LibraryTypeFilter>("all");
    const scopesEnabled = useScopesEnabled();
    const typeFilter =
        parseTypeFilter(selectedTypeFilter, scopesEnabled) ?? "all";
    if (typeFilter !== selectedTypeFilter) {
        setSelectedTypeFilter(typeFilter);
    }
    const [selectedFolder, setSelectedFolder] =
        useState<ILibraryListItem | null>(null);
    const [isMoving, setIsMoving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Client roots and the Shared root are never movable subjects (the backend
    // 400 is the backstop). They drop out of the move and the rest of the
    // selection proceeds; the notice says so, so a mixed selection is never a
    // silent dead end. When nothing is left to move the browser still shows
    // what was picked, and the confirm explains why it is unavailable.
    const movableItems = useMemo(
        () => items.filter((item) => !isProtectedRootListItem(item)),
        [items]
    );
    const protectedItems = useMemo(
        () => [
            ...items.filter(isProtectedRootListItem),
            ...(excludedItems ?? []),
        ],
        [items, excludedItems]
    );
    const subjectItems = movableItems.length > 0 ? movableItems : items;
    const nothingMovable = movableItems.length === 0;

    const protectedNotice = useMemo(() => {
        if (protectedItems.length === 0) return null;
        const label =
            protectedItems.length === 1
                ? `"${protectedItems[0].name}"`
                : `${protectedItems.length} ${
                      protectedItems.every((item) => item.type === "folder")
                          ? "folders"
                          : "items"
                  }`;
        return nothingMovable
            ? `${label} can't be moved.`
            : `${label} can't be moved and will stay put.`;
    }, [protectedItems, nothingMovable]);

    // Reset state when modal opens. Waits for folders to load so we can
    // correctly determine the initial tab (Documents vs KB). The ref guard
    // ensures we only initialize once per open cycle and don't reset when
    // folders or items change after the user has started navigating.
    const hasInitializedRef = useRef(false);
    useEffect(() => {
        if (!open) {
            hasInitializedRef.current = false;
            return;
        }
        if (hasInitializedRef.current) return;
        if (folders.length === 0) return;
        hasInitializedRef.current = true;

        const tab = getInitialTab(subjectItems, folders);
        const common = getCommonFolderId(subjectItems);
        setActiveTab(tab);
        setFolderId(
            tab === "knowledge_base" && common === null ? kbRootId : common
        );
        setSearchValue("");
        setSelectedTypeFilter("all");
        setSelectedFolder(null);
        setError(null);
    }, [open, subjectItems, folders, kbRootId]);

    const excludeFolderIds = useMemo(
        () => buildExcludeIds(subjectItems, folders),
        [subjectItems, folders]
    );

    // KB tab only when ALL items can go to KB (documents and folders).
    // Scopes/comparisons can't be moved to KB, so any mixed selection hides it.
    const showKbTab = useMemo(
        () =>
            subjectItems.length > 0 &&
            subjectItems.every(
                (i) => i.type === "document" || i.type === "folder"
            ),
        [subjectItems]
    );

    // KB root is represented by the tab toggle, not as a breadcrumb segment.
    // Passing it as currentFolder would create "Knowledge Base > Knowledge Base".
    const rawFolder = folders.find((f) => f.id === folderId) ?? null;
    const currentFolder =
        rawFolder?.is_knowledge_base && !rawFolder.parent_id ? null : rawFolder;

    const destinationFolderId = selectedFolder ? selectedFolder.id : folderId;

    const destinationLabel = useMemo(() => {
        if (selectedFolder) return selectedFolder.name;
        if (folderId === null) return "Documents";
        const folder = folders.find((f) => f.id === folderId);
        if (folder) return folder.name;
        return "selected folder";
    }, [selectedFolder, folderId, folders]);

    const handleTabChange = useCallback(
        (newTab: LibraryTab) => {
            setActiveTab(newTab);
            setFolderId(newTab === "knowledge_base" ? kbRootId : null);
            setSelectedFolder(null);
            setSearchValue("");
            setSelectedTypeFilter("all");
        },
        [kbRootId]
    );

    const handleFolderNavigate = useCallback((id: number | null) => {
        setFolderId(id);
        setSelectedFolder(null);
    }, []);

    const handleFolderSelect = useCallback(
        (folder: ILibraryListItem | null) => {
            setSelectedFolder(folder);
        },
        []
    );

    const handleSearchClear = useCallback(() => setSearchValue(""), []);

    const handleClose = useCallback(() => {
        if (isMoving) return;
        onClose();
    }, [isMoving, onClose]);

    const handleConfirm = useCallback(async () => {
        if (movableItems.length === 0) return;
        setIsMoving(true);
        setError(null);

        const results = await Promise.allSettled(
            movableItems.map((item) =>
                moveItemById(item.type, item.id, destinationFolderId)
            )
        );
        const failures = results.filter(
            (r): r is PromiseRejectedResult => r.status === "rejected"
        );
        const failed = failures.length;
        const succeeded = results.length - failed;

        setIsMoving(false);

        if (failed === 0) {
            enqueueSnackbar(
                `Moved ${succeeded} item${succeeded !== 1 ? "s" : ""}`,
                { variant: "success" }
            );
            invalidateLibraryQueries(queryClient);
            onClose();
            onMoved();
        } else if (succeeded > 0) {
            setError(
                `Moved ${succeeded} item${succeeded !== 1 ? "s" : ""}, but ${failed} failed. Please try again.`
            );
            invalidateLibraryQueries(queryClient);
        } else {
            // All failed: surface a backend guard's specific detail (e.g.
            // Unit 1's "Can't move a client's root folder") over the generic
            // copy — parity with moveWithUndo's all-failed toast. The menu
            // exclusions make guard 400s unreachable here EXCEPT through a
            // stale folder snapshot (another session's change landed after
            // this list was fetched); the backstop's message is then the
            // only honest explanation the user can get.
            setError(
                getApiErrorMessage(
                    failures[0].reason as IApiAxiosError,
                    `Failed to move ${failed} item${failed !== 1 ? "s" : ""}. Please try again.`
                )
            );
        }
    }, [movableItems, destinationFolderId, queryClient, onClose, onMoved]);

    const title =
        subjectItems.length === 1
            ? `Move ${subjectItems[0].type.charAt(0).toUpperCase()}${subjectItems[0].type.slice(1)}`
            : `Move ${subjectItems.length} Items`;

    const subtitle =
        subjectItems.length === 1
            ? `Moving "${subjectItems[0].name}"`
            : `Moving ${formatItemSummary(subjectItems)}`;

    const isSameDestination = useMemo(() => {
        if (movableItems.length === 0) return true;
        return movableItems.every((item) => {
            const currentFid =
                item.type === "folder"
                    ? item.parent_id
                    : item.library_folder_id;
            return currentFid === destinationFolderId;
        });
    }, [movableItems, destinationFolderId]);

    // Prevent moving a folder into itself or its own descendants.
    // This can happen via default-to-current-view when the user navigates
    // into the excluded subtree using the chevron.
    const isCycleDestination =
        destinationFolderId !== null &&
        excludeFolderIds.has(destinationFolderId);

    const actionLabel = isMoving ? "Moving…" : `Move to ${destinationLabel}`;

    const footerText = useMemo(
        () => (
            <Typography
                variant="body2"
                color="text.secondary"
                fontSize="0.8rem"
            >
                Destination:{" "}
                <Typography
                    component="span"
                    fontWeight={600}
                    color="text.primary"
                    fontSize="0.8rem"
                >
                    {destinationLabel}
                </Typography>
            </Typography>
        ),
        [destinationLabel]
    );

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title={title}
            subtitle={subtitle}
            maxWidth="lg"
            showCancel
            isLoading={isMoving}
            error={error}
            footerText={footerText}
            actions={[
                {
                    label: actionLabel,
                    onClick: handleConfirm,
                    disabled:
                        isMoving ||
                        isSameDestination ||
                        isCycleDestination ||
                        nothingMovable,
                },
            ]}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "60vh",
                    minHeight: 400,
                    overflow: "hidden",
                }}
            >
                {protectedNotice && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize="0.8rem"
                        sx={{ flexShrink: 0, mb: 1 }}
                    >
                        {protectedNotice}
                    </Typography>
                )}

                <Box
                    sx={{
                        flexShrink: 0,
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        bgcolor: "background.paper",
                    }}
                >
                    <FilterBar
                        activeTab={activeTab}
                        onTabChange={showKbTab ? handleTabChange : undefined}
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        onSearchClear={handleSearchClear}
                        typeFilter={typeFilter}
                        onTypeFilterChange={setSelectedTypeFilter}
                        currentFolder={currentFolder}
                        folders={folders}
                        onFolderNavigate={handleFolderNavigate}
                        hideActions
                        hideFilters={!showKbTab}
                    />
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                    <DocumentList
                        activeTab={activeTab}
                        folderId={folderId}
                        searchQuery={searchValue}
                        typeFilter={typeFilter}
                        mode="move"
                        selectionMode="single"
                        tableDensity="compact"
                        onFolderNavigate={handleFolderNavigate}
                        onFolderSelect={handleFolderSelect}
                        selectedFolderId={selectedFolder?.id ?? null}
                        excludeFolderIds={excludeFolderIds}
                        onSearchClear={handleSearchClear}
                        onDocumentAction={noop}
                        hideRowActions
                    />
                </Box>
            </Box>
        </Modal>
    );
};
